import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PatientFormFields, { EMPTY_PATIENT } from "@/components/PatientFormFields";

function PatientForm({ open, onClose, onSaved, patient }) {
  const isNew = !patient?.id;
  const [form, setForm] = useState(EMPTY_PATIENT);

  useEffect(() => {
    if (!open) return;
    if (isNew) setForm(EMPTY_PATIENT);
    else setForm({ ...EMPTY_PATIENT, ...patient });
  }, [open, patient, isNew]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isNew) await api.post("/patients", form);
      else await api.put(`/patients/${patient.id}`, form);
      toast.success(isNew ? "Paciente creado" : "Paciente actualizado");
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight text-2xl">{isNew ? "Nuevo paciente" : "Editar paciente"}</SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-6 pb-10" data-testid="patient-form">
          <PatientFormFields form={form} setForm={setForm} />
          <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 border-t border-border backdrop-blur">
            <button data-testid="patient-save" type="submit" className="btn-primary w-full justify-center">
              {isNew ? "Crear paciente" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

export default function Pacientes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/patients", { params: q ? { q } : {} });
    setItems(data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const onDelete = async (p) => {
    if (!window.confirm(`¿Eliminar paciente ${p.name}?`)) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Paciente eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Clínica</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Pacientes</h1>
          <p className="text-muted-foreground text-sm mt-3">{items.length} registros</p>
        </div>
        <button data-testid="new-patient-btn" className="btn-primary" onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nuevo paciente
        </button>
      </header>

      <div className="card-flat p-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-muted-foreground ml-2" />
        <input
          data-testid="patient-search"
          placeholder="Buscar por nombre, teléfono, documento o correo…"
          value={q} onChange={(e) => setQ(e.target.value)}
          className="flex-1 bg-transparent border-0 outline-none text-sm h-10"
        />
      </div>

      <div className="card-flat overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40">
          <div className="col-span-4 label-eyebrow">Nombre</div>
          <div className="col-span-2 label-eyebrow">Documento</div>
          <div className="col-span-2 label-eyebrow">Teléfono</div>
          <div className="col-span-2 label-eyebrow">EPS</div>
          <div className="col-span-1 label-eyebrow">Ciudad</div>
          <div className="col-span-1 label-eyebrow text-right">Acción</div>
        </div>
        {items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No hay pacientes registrados.</div>
        )}
        {items.map((p) => (
          <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-0 px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 transition md:items-center" data-testid={`patient-row-${p.id}`}>
            <div className="md:col-span-4 flex items-center justify-between gap-2 min-w-0">
              <Link to={`/pacientes/${p.id}`} className="font-semibold text-sm hover:text-primary truncate">{p.name}</Link>
              {user?.role === "admin" && (
                <button onClick={() => onDelete(p)} className="md:hidden p-2 -mr-2 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10" aria-label="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="md:col-span-2 font-mono text-xs text-muted-foreground truncate">
              {p.document_number ? `${p.document_type || ""} ${p.document_number}` : "—"}
            </div>
            <div className="md:col-span-2 font-mono text-xs md:text-sm text-muted-foreground truncate">{p.phone || "—"}</div>
            <div className="md:col-span-2 text-xs text-muted-foreground truncate flex md:block gap-2">
              <span>{p.eps || "—"}</span>
              <span className="md:hidden">· {p.city || "—"}</span>
            </div>
            <div className="hidden md:block col-span-1 text-xs text-muted-foreground truncate">{p.city || "—"}</div>
            <div className="hidden md:flex col-span-1 justify-end">
              {user?.role === "admin" && (
                <button onClick={() => onDelete(p)} className="p-2 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10" aria-label="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <PatientForm open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} patient={editing} />
    </div>
  );
}
